import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { IconTile } from '../../../shared/components/IconTile';
import { Pill } from '../../../shared/components/Pill';
import { SoftButton } from '../../../shared/components/SoftButton';
import type { SemanticColor } from '../../../shared/components/semanticColor';
import { formatAge } from '../../../shared/components/relativeTime';
import type { AiAssistantRole, Document, DocumentType } from '../../../core/types/data-models';

// ── Open tasks (sections in review + documents with unresolved comments) ────
//
// Each task is rendered as a short message "from" the AI Assistant tied to
// the document's category, in the same conversational style as Next Steps.
// New task kinds plug in by adding an entry to OPEN_TASK_PRESENTATION below —
// an icon plus a function that turns the task's context into a message.

export type OpenTask = { kind: 'review' | 'comments'; doc: Document; count: number };

const OPEN_TASK_PRESENTATION: Record<
  OpenTask['kind'],
  { icon: ReactNode; color: SemanticColor; countLabel: (count: number) => string }
> = {
  review: {
    icon: <RateReviewOutlinedIcon fontSize="inherit" />,
    color: 'warning',
    countLabel: (count) => `${count} Section${count === 1 ? '' : 's'}`,
  },
  comments: {
    icon: <ChatBubbleOutlineIcon fontSize="inherit" />,
    color: 'cyan',
    countLabel: (count) => `${count} Comment${count === 1 ? '' : 's'}`,
  },
};

export function OpenTasksSection({
  tasks,
  docTypeMap,
  roles,
  onOpenDocument,
}: {
  tasks: OpenTask[];
  docTypeMap: Map<string, DocumentType>;
  roles: AiAssistantRole[];
  onOpenDocument: (id: number) => void;
}) {
  const roleByCategory = new Map(roles.map((r) => [r.documentCategoryType, r]));

  return (
    <GlassPanel hover sx={{ p: 2.5, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', maxHeight: 380 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ pb: 1.5, mb: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <IconTile icon={<AssignmentOutlinedIcon fontSize="inherit" />} color="warning" size="sm" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              Open Tasks
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              Human-in-the-loop approvals required
            </Typography>
          </Box>
        </Stack>
        {tasks.length > 0 && (
          <Pill color="warning" sx={{ flexShrink: 0 }}>
            {tasks.length} Open
          </Pill>
        )}
      </Stack>

      {tasks.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
          No open tasks — everything is reviewed and resolved.
        </Typography>
      ) : (
        <Stack spacing={1.25} sx={{ overflowY: 'auto', pr: 0.5 }}>
          {tasks.map((task, i) => {
            const docType = docTypeMap.get(task.doc.documentType);
            const role = docType ? roleByCategory.get(docType.categoryType) : undefined;
            const presentation = OPEN_TASK_PRESENTATION[task.kind];
            return (
              <Stack
                key={`${task.kind}-${task.doc.id}-${i}`}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1.5}
                onClick={() => onOpenDocument(task.doc.id)}
                sx={{
                  p: 1.5,
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  boxShadow: (theme) => (theme.palette.mode === 'dark' ? 'none' : 'var(--elevation-sm)'),
                  cursor: 'pointer',
                  transition: 'border-color var(--transition)',
                  '&:hover': { borderColor: 'var(--color-primary-line)' },
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                  <IconTile icon={presentation.icon} color={presentation.color} size="md" />
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {task.doc.name}
                      </Typography>
                      <Pill color={presentation.color} sx={{ flexShrink: 0 }}>
                        {presentation.countLabel(task.count)}
                      </Pill>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {role?.name ?? 'AI Assistant'}
                      </Typography>
                      <Box component="span" sx={{ color: 'text.disabled', fontSize: 11 }}>
                        •
                      </Box>
                      <Typography variant="caption" color="text.disabled" noWrap>
                        {formatAge(task.doc.updatedAt)}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
                <SoftButton
                  tone="success"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenDocument(task.doc.id);
                  }}
                  sx={{ flexShrink: 0, fontSize: 11, fontWeight: 700 }}
                >
                  Review
                </SoftButton>
              </Stack>
            );
          })}
        </Stack>
      )}
    </GlassPanel>
  );
}
